import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, MapPin, Calendar, Clock, Users, Luggage, Route, Timer, Info, ChevronUp, ChevronDown, Plane, Repeat, ArrowLeftRight, LocateFixed, Plus, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { AddressInput } from "@/components/AddressInput";
import { AvailabilityCalendar } from "@/components/AvailabilityCalendar";
import { RouteMap } from "@/components/RouteMap";
import { TIME_SLOTS } from "@/components/QuoteWidget";

const inputCls =
  "w-full bg-[#1E2128] border border-[#2E3340] focus:border-amber-500/60 outline-none px-4 py-3.5 text-sm font-medium text-zinc-100 placeholder:text-zinc-500 transition-colors duration-300";

export const hourlyRate = (h) => (h <= 1 ? 210 : { 2: 160, 3: 140, 4: 120 }[h] ?? 100);

const Row = ({ label, value, testid }) => (
  <div className="flex justify-between gap-4 text-sm border-b border-[#2E3340]/60 pb-3">
    <span className="text-zinc-300 font-medium">{label}</span>
    <span className="text-zinc-100 font-medium text-right" data-testid={testid}>{value}</span>
  </div>
);

export default function Booking() {
  const [params] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [confirmed, setConfirmed] = useState(null);
  const [sending, setSending] = useState(false);
  const [fare, setFare] = useState(null);
  const [fareError, setFareError] = useState("");
  const [fareLoading, setFareLoading] = useState(false);
  const [showLegend, setShowLegend] = useState(false);
  const [addReturn, setAddReturn] = useState(false);
  const [stops, setStops] = useState(() => (params.get("stops") || "").split("|").filter(Boolean).slice(0, 3));
  const [addonsCfg, setAddonsCfg] = useState({ addons: [], stop_fee: 25 });
  const [selectedAddons, setSelectedAddons] = useState(() => (params.get("addons") || "").split(",").filter(Boolean));
  const [codeInput, setCodeInput] = useState(params.get("code") || "");
  const [appliedCode, setAppliedCode] = useState(params.get("code") || "");
  const debounceRef = useRef(null);

  const [form, setForm] = useState({
    vehicle_id: params.get("vehicle") || "",
    mode: params.get("mode") === "hourly" ? "hourly" : "transfer",
    pickup: params.get("pickup") || "",
    dropoff: params.get("dropoff") || "",
    date: params.get("date") || "",
    time: params.get("time") || "",
    hours: Number(params.get("hours")) || 2,
    passengers: 1,
    luggage: 0,
    name: "",
    email: "",
    phone: "",
    notes: "",
    flight_number: "",
    return_date: "",
    return_time: "",
  });

  useEffect(() => {
    api.get("/vehicles").then((r) => {
      setVehicles(r.data);
      if (!form.vehicle_id && r.data.length) setForm((f) => ({ ...f, vehicle_id: r.data[0].id }));
    });
    api.get("/addons").then((r) => setAddonsCfg(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const vehicle = vehicles.find((v) => v.id === form.vehicle_id);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const hours = Math.max(Number(form.hours) || 1, 1);
  const perHour = hourlyRate(hours);
  const stopKey = stops.filter(Boolean).join("|");

  useEffect(() => {
    const isTransfer = form.mode === "transfer";
    if (isTransfer && (!form.pickup.trim() || !form.dropoff.trim())) {
      setFare(null);
      setFareError("");
      return;
    }
    setFareLoading(true);
    setFareError("");
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/pricing/estimate", {
          mode: form.mode,
          pickup: form.pickup,
          dropoff: form.dropoff,
          stops: stops.filter(Boolean),
          hours,
          add_ons: selectedAddons,
          code: appliedCode,
        });
        setFare(data);
      } catch (err) {
        setFare(null);
        setFareError(formatApiError(err));
      } finally {
        setFareLoading(false);
      }
    }, 700);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.mode, form.pickup, form.dropoff, stopKey, form.hours, selectedAddons, appliedCode]);

  const returnFare = addReturn && fare && form.mode === "transfer" ? Math.round(fare.base * 0.9) : 0;
  const estimate = fare ? Math.round((fare.price + returnFare) * 100) / 100 : null;

  const [blackoutDates, setBlackoutDates] = useState([]);
  const [locating, setLocating] = useState(false);
  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Your browser can't share location — please type the address.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await api.get("/geo/reverse", { params: { lat: pos.coords.latitude, lng: pos.coords.longitude } });
          setForm((f) => ({ ...f, pickup: data.label }));
          toast.success("Location found — pickup address filled in.");
        } catch (err) {
          toast.error(formatApiError(err));
        } finally {
          setLocating(false);
        }
      },
      () => {
        setLocating(false);
        toast.error("Location unavailable — allow location access, or type the address.");
      },
      { timeout: 10000, maximumAge: 60000 }
    );
  };
  useEffect(() => {
    if (!form.vehicle_id) {
      setBlackoutDates([]);
      return;
    }
    api.get("/blackouts", { params: { vehicle_id: form.vehicle_id } })
      .then((r) => setBlackoutDates(r.data.map((b) => b.date)))
      .catch(() => {});
  }, [form.vehicle_id]);
  const dateBlocked = Boolean(form.date && blackoutDates.includes(form.date));
  const returnBlocked = Boolean(addReturn && form.return_date && blackoutDates.includes(form.return_date));

  const canSubmit =
    form.vehicle_id && form.pickup && form.date && form.time && form.name && form.email && fare &&
    (form.mode === "hourly" || form.dropoff) &&
    (!addReturn || (form.return_date && form.return_time)) &&
    !fareLoading && !dateBlocked && !returnBlocked;

  const submit = async () => {
    setSending(true);
    try {
      const payload = {
        ...form,
        hours,
        passengers: Number(form.passengers),
        luggage: Number(form.luggage),
        stops: stops.filter(Boolean),
        add_ons: selectedAddons,
        code: appliedCode,
        distance_miles: fare?.miles ?? 0,
        duration_minutes: fare?.duration_minutes ?? 60,
        return_date: addReturn ? form.return_date : "",
        return_time: addReturn ? form.return_time : "",
      };
      const { data } = await api.post("/bookings", payload);
      setConfirmed(data);
      toast.success("Reservation received.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const [paying, setPaying] = useState("");
  const depositPreview = (est) => Math.min(Math.max(50, Math.round(est * 0.2 * 100) / 100), est);
  const payDeposit = async (reference) => {
    setPaying(reference);
    try {
      const { data } = await api.post("/payments/deposit", {
        booking_reference: reference,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setPaying("");
    }
  };

  if (confirmed) {
    const ret = confirmed.return_booking;
    return (
      <main className="min-h-screen bg-[#0F1012] flex items-center justify-center px-6" data-testid="booking-confirmation">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-lg w-full border border-[#2E3340] bg-[#1E2128] p-10 text-center"
        >
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-full border border-amber-500/40 mb-6">
            <Check className="text-amber-400" size={22} />
          </span>
          <p className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.35em] text-amber-500">Reservation Received</p>
          <h1 className="font-serif-lux text-4xl text-amber-50 mt-3">Consider it arranged.</h1>
          <p className="mt-4 text-sm text-zinc-300 leading-relaxed">
            Reference <span className="text-amber-300 font-mono-lux" data-testid="booking-reference">{confirmed.reference}</span> —
            our concierge will confirm {confirmed.vehicle_name} for {confirmed.date} at {confirmed.time} shortly.
            A confirmation email is on its way to you.
          </p>
          {confirmed.stops?.length > 0 && (
            <p className="mt-3 text-xs text-zinc-400" data-testid="confirmation-stops">
              Stops: {confirmed.stops.join(" · ")}
            </p>
          )}
          {confirmed.add_ons?.length > 0 && (
            <p className="mt-2 text-sm text-zinc-300" data-testid="confirmation-addons">
              Add-ons: {confirmed.add_ons.map((a) => a.label).join(" · ")}
            </p>
          )}
          {confirmed.discount_amount > 0 && (
            <p className="mt-2 text-xs font-medium text-emerald-400" data-testid="confirmation-discount">
              {confirmed.promo_code ? `Promo ${confirmed.promo_code}` : `Gift voucher ${confirmed.voucher_code}`} saved you ${confirmed.discount_amount}.
            </p>
          )}
          {ret && (
            <p className="mt-3 text-sm text-zinc-300" data-testid="return-reference">
              Return leg <span className="text-amber-300 font-mono-lux">{ret.reference}</span> — {ret.date} at {ret.time},{" "}
              {ret.pickup} → {ret.dropoff} · <span className="text-emerald-400">10% round-trip saving applied</span>
            </p>
          )}
          <p className="mt-2 text-sm text-zinc-400">
            Estimated fare: <span className="gold-text font-serif-lux text-xl" data-testid="confirmation-total">${confirmed.estimate + (ret?.estimate ?? 0)}</span>
          </p>
          <div className="mt-8 border border-amber-500/30 bg-[#0F1012] p-6 text-left" data-testid="deposit-card">
            <p className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-500">Secure It Instantly</p>
            <p className="mt-2 text-xs text-zinc-400 leading-relaxed">
              Lock in your chauffeur now with a 20% deposit — the balance is settled after your journey.
              Prefer to wait? Your concierge confirms every fare personally either way.
            </p>
            <button
              onClick={() => payDeposit(confirmed.reference)}
              disabled={!!paying}
              data-testid="pay-deposit-button"
              className="mt-4 w-full font-mono-lux text-[10px] font-semibold uppercase tracking-[0.25em] px-6 py-3.5 bg-amber-500 text-black hover:bg-amber-300 transition-colors disabled:opacity-50"
            >
              {paying === confirmed.reference ? "Preparing secure checkout…" : `Pay deposit — $${depositPreview(confirmed.estimate)}`}
            </button>
            {ret && (
              <button
                onClick={() => payDeposit(ret.reference)}
                disabled={!!paying}
                data-testid="pay-deposit-return-button"
                className="mt-3 w-full font-mono-lux text-[10px] font-semibold uppercase tracking-[0.25em] px-6 py-3.5 border border-amber-500/40 text-amber-300 hover:bg-amber-500 hover:text-black transition-colors disabled:opacity-50"
              >
                {paying === ret.reference ? "Preparing secure checkout…" : `Return leg deposit — $${depositPreview(ret.estimate)}`}
              </button>
            )}
            <p className="mt-3 text-[10px] text-zinc-600 text-center">Secure card payment via Stripe · Visa, Mastercard, Amex</p>
          </div>
          <div className="mt-8 flex gap-3 justify-center">
            <Link
              to="/"
              data-testid="confirmation-home-link"
              className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.3em] px-6 py-3 border border-amber-500/40 text-amber-300 hover:bg-amber-500 hover:text-black transition-colors"
            >
              Return Home
            </Link>
            <button
              onClick={() => { setConfirmed(null); setFare(null); setAddReturn(false); }}
              data-testid="confirmation-new-booking-button"
              className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.3em] px-6 py-3 border border-[#2E3340] text-zinc-400 hover:border-amber-500/40 transition-colors"
            >
              New Reservation
            </button>
          </div>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#0F1012] grain relative" data-testid="booking-page">
      <header className="border-b border-[#2E3340]">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <Logo />
          <Link
            to="/"
            data-testid="booking-back-home"
            className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.3em] text-zinc-400 hover:text-amber-400 transition-colors"
          >
            ← Back
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-14 lg:py-20">
        <p className="font-mono-lux text-[11px] font-semibold uppercase tracking-[0.4em] text-amber-500">Reserve a Journey</p>
        <h1 className="font-serif-lux font-normal text-4xl sm:text-5xl text-amber-50 mt-3">
          Compose your <span className="gold-text italic">itinerary.</span>
        </h1>

        {vehicle && (
          <div className="mt-10 border border-[#2E3340] bg-[#1E2128] overflow-hidden flex flex-col sm:flex-row" data-testid="booking-vehicle-banner">
            <div className="sm:w-72 aspect-[16/10] sm:aspect-auto overflow-hidden shrink-0">
              <img src={vehicle.image} alt={vehicle.name} className="w-full h-full object-cover" />
            </div>
            <div className="p-6 flex-1 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-500">Your Vehicle</p>
                <h2 className="font-serif-lux text-2xl font-medium text-amber-50 mt-1">{vehicle.name}</h2>
                <p className="mt-1 flex items-center gap-4 text-[11px] font-medium text-zinc-400">
                  <span className="flex items-center gap-1"><Users size={12} /> {vehicle.passengers} guests</span>
                  <span className="flex items-center gap-1"><Luggage size={12} /> {vehicle.luggage} cases</span>
                </p>
              </div>
              <p className="text-right">
                <span className="font-serif-lux text-2xl gold-text">${vehicle.hourly_rate}</span>
                <span className="block font-mono-lux text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">per hour · tiers to $100</span>
              </p>
            </div>
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 space-y-6">
            <div className="grid grid-cols-2 border border-[#2E3340]" data-testid="booking-mode-tabs">
              {[
                { id: "transfer", label: "PTP/Transfer", sub: "from $5 / mile", icon: Route },
                { id: "hourly", label: "By the Hour", sub: "from $100 / hr", icon: Timer },
              ].map((m) => (
                <button
                  type="button"
                  key={m.id}
                  onClick={() => set("mode")(m.id)}
                  data-testid={`booking-mode-${m.id}`}
                  className={`py-5 text-center transition-colors duration-300 ${
                    form.mode === m.id ? "bg-amber-500 text-black" : "bg-[#1E2128] text-zinc-300 hover:text-amber-200"
                  }`}
                >
                  <m.icon size={18} className="mx-auto mb-2" strokeWidth={1.5} />
                  <span className="block font-serif-lux text-xl font-medium">{m.label}</span>
                  <span className="block font-mono-lux text-[10px] font-semibold uppercase tracking-[0.2em] mt-1 opacity-80">{m.sub}</span>
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="block">
                <span className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-300 flex items-center gap-2 mb-2">
                  <MapPin size={12} className="text-amber-500" /> Pickup
                  <button
                    type="button"
                    onClick={useCurrentLocation}
                    disabled={locating}
                    data-testid="booking-use-location"
                    className="ml-auto inline-flex items-center gap-1.5 text-zinc-500 hover:text-amber-400 transition-colors duration-300 disabled:opacity-50"
                  >
                    <LocateFixed size={11} strokeWidth={1.5} /> {locating ? "Locating…" : "Current location"}
                  </button>
                </span>
                <AddressInput
                  value={form.pickup}
                  onChange={(v) => setForm((f) => ({ ...f, pickup: v }))}
                  placeholder="e.g. Palm Beach Island"
                  className={inputCls}
                  testId="booking-pickup-input"
                />
              </div>
              {form.mode === "transfer" && (
                <div className="block">
                  <span className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-300 flex items-center gap-2 mb-2">
                    <MapPin size={12} className="text-amber-500" /> Destination
                    <button
                      type="button"
                      onClick={() => {
                        setForm((f) => ({ ...f, pickup: f.dropoff, dropoff: f.pickup }));
                        setStops((s) => [...s].reverse());
                      }}
                      data-testid="booking-swap-addresses"
                      className="ml-auto inline-flex items-center gap-1.5 text-zinc-500 hover:text-amber-400 transition-colors duration-300"
                    >
                      <ArrowLeftRight size={11} strokeWidth={1.5} /> Swap
                    </button>
                  </span>
                  <AddressInput
                    value={form.dropoff}
                    onChange={(v) => setForm((f) => ({ ...f, dropoff: v }))}
                    placeholder="e.g. Miami Intl (MIA)"
                    className={inputCls}
                    testId="booking-dropoff-input"
                  />
                </div>
              )}
              {form.mode === "hourly" && (
                <label className="block">
                  <span className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-300 mb-2 block">
                    Hours <span className="text-amber-400 normal-case tracking-normal">— longer bookings earn lower rates</span>
                  </span>
                  <select value={form.hours} onChange={set("hours")} className={inputCls} data-testid="booking-hours-input">
                    {[1, 2, 3, 4, 5, 6, 8, 10, 12].map((h) => (
                      <option key={h} value={h}>
                        {h} hour{h > 1 ? "s" : ""} — ${hourlyRate(h)}/hr
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label className="block">
                <span className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-300 flex items-center gap-2 mb-2">
                  <Calendar size={12} className="text-amber-500" /> Date
                </span>
                <input type="date" value={form.date} onChange={set("date")} className={inputCls} data-testid="booking-date-input" />
                {dateBlocked && (
                  <p className="mt-2 text-[11px] text-red-400" data-testid="date-blocked-note">
                    That date is unavailable for this vehicle — please choose another day.
                  </p>
                )}
              </label>
              <label className="block">
                <span className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-300 flex items-center gap-2 mb-2">
                  <Clock size={12} className="text-amber-500" /> Time
                </span>
                <select value={form.time} onChange={set("time")} className={inputCls} data-testid="booking-time-input">
                  <option value="" disabled>Select time</option>
                  {TIME_SLOTS.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-300 mb-2 block">Passengers</span>
                <input type="number" min={1} max={vehicle?.passengers ?? 8} value={form.passengers} onChange={set("passengers")} className={inputCls} data-testid="booking-passengers-input" />
              </label>
              <label className="block">
                <span className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-300 mb-2 block">Luggage</span>
                <input type="number" min={0} max={vehicle ? vehicle.luggage + 2 : 6} value={form.luggage} onChange={set("luggage")} className={inputCls} data-testid="booking-luggage-input" />
              </label>
              <label className="block sm:col-span-2">
                <span className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-300 flex items-center gap-2 mb-2">
                  <Plane size={12} className="text-amber-500" /> Flight Number <span className="text-zinc-500 normal-case tracking-normal">(optional — airport arrivals are tracked in every mode)</span>
                </span>
                <input value={form.flight_number} onChange={set("flight_number")} placeholder="e.g. AA1234" className={inputCls} data-testid="booking-flight-input" />
              </label>
            </div>

            <AvailabilityCalendar
              vehicleId={form.vehicle_id}
              value={form.date}
              onSelect={(d) => setForm((f) => ({ ...f, date: d }))}
            />

            {form.mode === "transfer" && (
              <div className="border border-[#2E3340] bg-[#1E2128] p-5" data-testid="stops-block">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-300 flex items-center gap-2">
                    <Route size={12} className="text-amber-500" /> Additional stops
                    <span className="text-zinc-500 normal-case tracking-normal">— up to 3 · ${addonsCfg.stop_fee} per stop</span>
                  </span>
                  {stops.length < 3 && (
                    <button
                      type="button"
                      onClick={() => setStops((s) => [...s, ""])}
                      data-testid="add-stop-button"
                      className="inline-flex items-center gap-1.5 font-mono-lux text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-400 hover:text-amber-200 transition-colors"
                    >
                      <Plus size={11} /> Add stop
                    </button>
                  )}
                </div>
                {stops.length === 0 && (
                  <p className="mt-3 text-[11px] font-medium text-zinc-500">
                    Direct route. Add a stop and the fare recomputes on the real itinerary.
                  </p>
                )}
                <div className="mt-3 space-y-3">
                  {stops.map((s, i) => (
                    <div key={i} className="flex gap-2 items-start">
                      <div className="flex-1">
                        <AddressInput
                          value={s}
                          onChange={(v) => setStops((arr) => arr.map((x, j) => (j === i ? v : x)))}
                          placeholder={`Stop ${i + 1} — e.g. The Breakers`}
                          className={inputCls}
                          testId={`booking-stop-${i}-input`}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setStops((arr) => arr.filter((_, j) => j !== i))}
                        data-testid={`remove-stop-${i}`}
                        className="mt-3.5 text-zinc-500 hover:text-red-400 transition-colors"
                        aria-label={`Remove stop ${i + 1}`}
                      >
                        <X size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {form.mode === "transfer" && (
              <div className="border border-[#2E3340] bg-[#1E2128] p-5" data-testid="return-trip-block">
                <button
                  type="button"
                  onClick={() => setAddReturn((s) => !s)}
                  data-testid="return-trip-toggle"
                  className="flex items-center gap-3 w-full text-left"
                >
                  <span className={`w-5 h-5 border flex items-center justify-center transition-colors ${addReturn ? "bg-amber-500 border-amber-500 text-black" : "border-amber-500/70 text-transparent hover:border-amber-400"}`}>
                    <Check size={13} />
                  </span>
                  <Repeat size={15} className="text-amber-500" />
                  <span className="font-serif-lux text-lg text-amber-50">Add the return journey</span>
                  <span className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-400 ml-auto">save 10%</span>
                </button>
                {addReturn && (
                  <div className="grid grid-cols-2 gap-4 mt-5">
                    <label className="block">
                      <span className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-300 mb-2 block">Return Date</span>
                      <input type="date" value={form.return_date} onChange={set("return_date")} className={inputCls} data-testid="return-date-input" />
                    {returnBlocked && (
                      <p className="mt-2 text-[11px] text-red-400" data-testid="return-date-blocked-note">
                        That return date is unavailable — please choose another day.
                      </p>
                    )}
                    </label>
                    <label className="block">
                      <span className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-300 mb-2 block">Return Time</span>
                      <select value={form.return_time} onChange={set("return_time")} className={inputCls} data-testid="return-time-input">
                        <option value="" disabled>Select time</option>
                        {TIME_SLOTS.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </label>
                    <p className="sm:col-span-2 text-[11px] font-medium text-zinc-500">
                      Same route in reverse: {form.dropoff || "destination"} → {form.pickup || "pickup"} at 10% off the route fare.
                    </p>
                  </div>
                )}
              </div>
            )}

            {form.mode === "transfer" && fare && !fareLoading && (
              <RouteMap pickup={form.pickup} dropoff={form.dropoff} stops={stops} />
            )}

            {addonsCfg.addons.length > 0 && (
              <div className="border border-[#2E3340] bg-[#1E2128] p-5" data-testid="addons-block">
                <span className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-300">Concierge add-ons</span>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {addonsCfg.addons.map((a) => {
                    const on = selectedAddons.includes(a.key);
                    return (
                      <button
                        type="button"
                        key={a.key}
                        onClick={() => setSelectedAddons((sel) => (on ? sel.filter((k) => k !== a.key) : [...sel, a.key]))}
                        data-testid={`addon-${a.key}`}
                        className={`flex items-center justify-between gap-3 border px-4 py-3 text-left transition-colors duration-300 ${on ? "border-amber-500 bg-amber-500/10" : "border-[#2E3340] hover:border-amber-500/40"}`}
                      >
                        <span className={`text-xs font-medium ${on ? "text-amber-200" : "text-zinc-300"}`}>{a.label}</span>
                        <span className={`font-mono-lux text-[10px] tracking-[0.1em] flex items-center gap-1 ${on ? "text-amber-400" : "text-zinc-500"}`}>
                          {on && <Check size={12} />} ${a.price}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input value={form.name} onChange={set("name")} placeholder="Full name" className={inputCls} data-testid="booking-name-input" />
              <input type="email" value={form.email} onChange={set("email")} placeholder="Email address" className={inputCls} data-testid="booking-email-input" />
            </div>
            <input value={form.phone} onChange={set("phone")} placeholder="Telephone" className={inputCls} data-testid="booking-phone-input" />
            <textarea
              value={form.notes}
              onChange={set("notes")}
              rows={4}
              placeholder="Special requests — security considerations, preferred route, a particular vintage…"
              className={`${inputCls} resize-none`}
              data-testid="booking-notes-input"
            />
          </div>

          <div className="lg:col-span-2">
            <div className="lg:sticky lg:top-8 border border-[#2E3340] bg-[#1E2128] p-7" data-testid="booking-fare-panel">
              <p className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.35em] text-amber-500">Your Fare</p>
              {fareLoading && (
                <p className="mt-5 font-mono-lux text-[11px] font-semibold uppercase tracking-[0.25em] text-zinc-400 animate-pulse" data-testid="fare-loading">
                  {form.mode === "transfer" ? "Mapping your route…" : "Composing your fare…"}
                </p>
              )}
              {fareError && !fareLoading && <p className="mt-5 text-xs font-medium text-red-400" data-testid="fare-error">{fareError}</p>}
              {!fare && !fareLoading && !fareError && (
                <p className="mt-5 text-sm font-medium text-zinc-400" data-testid="fare-empty">
                  {form.mode === "transfer"
                    ? "Enter pickup and destination for an exact fare — $5–$10 per mile by distance, $50 minimum."
                    : "Your hourly fare composes itself as you choose the duration."}
                </p>
              )}
              {fare && !fareLoading && (
                <div className="mt-5 space-y-4">
                  {form.mode === "transfer" ? (
                    <>
                      <Row label="Distance" value={`${fare.miles} mi · ~${fare.duration_minutes} min`} testid="fare-distance" />
                      <Row label="Rate" value={`$${fare.per_mile} / mile`} testid="fare-rate" />
                      {fare.minimum_applied && (
                        <p className="text-[11px] font-medium text-amber-400" data-testid="fare-minimum-note">
                          $50 minimum fare applied to this short hop.
                        </p>
                      )}
                      {fare.stop_fees > 0 && (
                        <Row label={`${fare.stops.length} stop${fare.stops.length > 1 ? "s" : ""}`} value={`$${fare.stop_fees}`} testid="fare-stops" />
                      )}
                    </>
                  ) : (
                    <>
                      <Row label="Duration" value={`${fare.hours} hour${fare.hours > 1 ? "s" : ""}`} />
                      <Row label="Tier rate" value={`$${fare.per_hour} / hour`} testid="fare-tier-rate" />
                      {fare.per_hour < 210 && (
                        <p className="text-[11px] font-medium text-emerald-400" data-testid="fare-tier-savings">
                          Tier discount applied — save ${(210 - fare.per_hour) * fare.hours} vs the single-hour rate.
                        </p>
                      )}
                    </>
                  )}
                  {(fare.add_ons || []).map((a) => (
                    <Row key={a.key} label={a.label} value={`$${a.price}`} testid={`fare-addon-${a.key}`} />
                  ))}
                  {(fare.add_ons_total > 0 || fare.stop_fees > 0) && (
                    <Row label="Subtotal" value={`$${fare.subtotal}`} testid="fare-subtotal" />
                  )}
                  {fare.promo_discount > 0 && (
                    <div className="flex justify-between gap-4 text-sm border-b border-[#2E3340]/60 pb-3" data-testid="fare-promo">
                      <span className="text-emerald-400 font-medium">Promo {fare.promo_code} · {fare.promo_label}</span>
                      <span className="text-emerald-400 font-medium">−${fare.promo_discount}</span>
                    </div>
                  )}
                  {fare.voucher_credit > 0 && (
                    <div className="flex justify-between gap-4 text-sm border-b border-[#2E3340]/60 pb-3" data-testid="fare-voucher">
                      <span className="text-emerald-400 font-medium">Gift voucher {fare.voucher_code}</span>
                      <span className="text-emerald-400 font-medium">−${fare.voucher_credit}</span>
                    </div>
                  )}
                  {fare.code_error && (
                    <p className="text-[11px] font-medium text-red-400" data-testid="fare-code-error">{fare.code_error}</p>
                  )}
                  {addReturn && form.mode === "transfer" && (
                    <>
                      <Row label="Outbound" value={`$${fare.price}`} testid="fare-outbound" />
                      <div className="flex justify-between gap-4 text-sm border-b border-[#2E3340]/60 pb-3">
                        <span className="text-zinc-300 font-medium">Return · 10% off</span>
                        <span className="text-zinc-100 font-medium" data-testid="fare-return">
                          <span className="line-through text-zinc-500 mr-2">${fare.base}</span>${returnFare}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="mt-5">
                <div className="flex gap-2">
                  <input
                    value={codeInput}
                    onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                    placeholder="Promo or gift code"
                    className="flex-1 bg-[#0F1012] border border-[#2E3340] focus:border-amber-500/60 outline-none px-3.5 py-2.5 text-xs font-medium text-zinc-100 placeholder:text-zinc-600 transition-colors"
                    data-testid="booking-code-input"
                  />
                  {appliedCode ? (
                    <button
                      type="button"
                      onClick={() => { setAppliedCode(""); setCodeInput(""); }}
                      data-testid="booking-code-clear"
                      className="px-3 border border-[#2E3340] text-zinc-400 hover:border-red-500/50 hover:text-red-400 transition-colors"
                      aria-label="Remove code"
                    >
                      <X size={14} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setAppliedCode(codeInput.trim())}
                      disabled={!codeInput.trim()}
                      data-testid="booking-code-apply"
                      className="px-4 font-mono-lux text-[10px] font-bold uppercase tracking-[0.2em] border border-amber-500/40 text-amber-300 hover:bg-amber-500 hover:text-black transition-colors disabled:opacity-40"
                    >
                      Apply
                    </button>
                  )}
                </div>
                {appliedCode && fare && !fare.code_error && (fare.promo_discount > 0 || fare.voucher_credit > 0) && (
                  <p className="mt-2 text-[11px] font-medium text-emerald-400" data-testid="code-applied-note">
                    {fare.promo_code
                      ? `${fare.promo_code} applied — ${fare.promo_label}.`
                      : `${fare.voucher_code} applied — $${fare.voucher_credit} credit.`}
                  </p>
                )}
              </div>

              <div className="mt-6 flex items-end justify-between">
                <span className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.3em] text-zinc-400">
                  {addReturn && form.mode === "transfer" ? "Total Estimate" : "Estimated Fare"}
                </span>
                <span className="font-serif-lux text-4xl gold-text" data-testid="booking-estimate">
                  {estimate != null ? `$${estimate}` : "—"}
                </span>
              </div>
              {estimate != null && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setShowLegend((s) => !s)}
                    data-testid="fare-legend-toggle"
                    className="flex items-center gap-2 font-mono-lux text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-400 hover:text-amber-200 transition-colors"
                  >
                    <Info size={12} /> {showLegend ? "Hide fare guide" : "How this fare works"}
                    {showLegend ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                  {showLegend && (
                    <div className="mt-3 border border-[#2E3340] divide-y divide-[#2E3340]/60" data-testid="fare-legend">
                      {(form.mode === "transfer"
                        ? [
                            { label: "$10 / mile", range: "Up to 10 miles", active: fare && fare.miles <= 10 },
                            { label: "$7 / mile", range: "11 – 20 miles", active: fare && fare.miles > 10 && fare.miles <= 20 },
                            { label: "$6 / mile", range: "21 – 29 miles", active: fare && fare.miles > 20 && fare.miles < 30 },
                            { label: "$5 / mile", range: "30 – 150 miles", active: fare && fare.miles >= 30 },
                            { label: "$50", range: "Minimum per pickup", active: fare?.minimum_applied },
                            { label: `$${addonsCfg.stop_fee}`, range: "Per additional stop", active: fare?.stop_fees > 0 },
                            { label: "10% off", range: "Return leg booked together", active: addReturn },
                          ]
                        : [
                            { label: "$210 / hr", range: "Single hour", active: hours === 1 },
                            { label: "$160 / hr", range: "2 hours", active: hours === 2 },
                            { label: "$140 / hr", range: "3 hours", active: hours === 3 },
                            { label: "$120 / hr", range: "4 hours", active: hours === 4 },
                            { label: "$100 / hr", range: "5+ hours", active: hours >= 5 },
                          ]
                      ).map((r) => (
                        <div
                          key={r.label}
                          className={`flex justify-between items-center px-4 py-2.5 text-xs font-medium ${
                            r.active ? "bg-amber-500/10 text-amber-300" : "text-zinc-400"
                          }`}
                          data-testid={r.active ? "fare-legend-active-tier" : undefined}
                        >
                          <span className="font-mono-lux tracking-[0.15em]">{r.label}</span>
                          <span className="flex items-center gap-2">
                            {r.range}
                            {r.active && <Check size={11} className="text-amber-400" />}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <p className="mt-3 text-[11px] font-medium text-zinc-500">
                Chauffeur, fuel and tolls included. Your concierge confirms every fare personally.
              </p>
              <button
                onClick={submit}
                disabled={!canSubmit || sending}
                data-testid="booking-confirm-button"
                className="mt-6 w-full flex items-center justify-center gap-2 font-mono-lux text-[11px] font-bold uppercase tracking-[0.3em] px-8 py-4 bg-amber-500 text-black hover:bg-amber-300 disabled:opacity-40 transition-colors"
              >
                {sending ? "Reserving…" : "Confirm Reservation"} <Check size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
